import { z } from 'zod';
import { getGatewayInfo } from '@/lib/clients/ddc/gateway-info';
import { getMarketplaceConfig } from '@/lib/clients/marketplace/marketplace-config';
import { readBuildEnv } from '@/src/config/buildEnv';
import { WALLET_ORIGINS } from '@/lib/platform-client';
import type { PlatformEnv } from '@/lib/platform-client';

const Schema = z.object({
  VITE_VAULT_BASE_URL: z.string().url(),
  VITE_MARKETPLACE_BASE_URL: z.string().url(),
  VITE_S3_ENDPOINT: z.string().url().optional(),
  // DDC content gateway that serves `cef push` widget bundles + bucket
  // manifests (`{cdn}/{bucketId}/agents/...` and `{cdn}/{bucketId}/{cid}/{entry}`).
  // Env-specific — dev is `https://cdn.devnet.ddc-dragon.com` (see .env.dev).
  VITE_DDC_CDN_URL: z.string().url().default('https://cdn.testnet.cere.network'),
  // Orchestrator API — used to resolve an agent service's numeric DDC bucket id
  // (`GET /api/v1/agent-services/:pubkey`) so we can read its bucket manifest.
  VITE_DDC_COMPUTE_URL: z.string().url().default('https://orchestrator.compute.dev.ddcdragon.com'),
  VITE_GAR_URL: z.string().url().default('https://gar.compute.dev.ddcdragon.com/'),
  VITE_RPC_ENDPOINT: z.string().url().default('wss://rpc.devnet.cere.network/ws'),
  // Drives the platform env (local|dev|stage). REQUIRED — there is
  // intentionally no default. A missing value here would mount the wrong
  // wallet origin for the build, so we fail loudly at parse time. Deploys
  // inject this via window.__RUNTIME_CONFIG__ (the cef-vault-ui ConfigMap);
  // local dev falls back to `.env.local`. The two deployable profiles map to
  // the two cells: "dev" is dragon1-devnet, "stage" is dragon1-testnet —
  // whose wallet serves at wallet.compute.test.* (see WALLET_ORIGINS).
  VITE_APP_ENV: z.enum(['local', 'dev', 'stage']),
});

// Derived from the schema so the key list can never drift from it: adding a
// field to Schema automatically makes it resolve from window/build-env.
const CONFIG_KEYS = Object.keys(Schema.shape) as Array<keyof typeof Schema.shape>;

/**
 * Resolve one config key. Precedence:
 *   1. window.__RUNTIME_CONFIG__[key] — deployed /config.js (env-agnostic image).
 *   2. import.meta.env[key] — Vite build-time, for local dev / tests.
 */
function readConfig(key: string): string | undefined {
  const fromWindow =
    typeof window !== 'undefined' && window.__RUNTIME_CONFIG__
      ? window.__RUNTIME_CONFIG__[key]
      : undefined;
  if (typeof fromWindow === 'string' && fromWindow.length > 0) return fromWindow;
  return readBuildEnv(key);
}

function collectEnv(): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const key of CONFIG_KEYS) out[key] = readConfig(key);
  return out;
}

export interface RuntimeConfig {
  vaultBaseUrl: string;
  marketplaceBaseUrl: string;
  ddcCdnUrl: string;
  ddcComputeUrl: string;
  s3Endpoint: string | null;
  gatewayPublicKey: string | null;
  clusterId: string | null;
  region: string | null;
  garUrl: string;
  rpcEndpoint: string;
  /** Which platform env this build targets (local/dev/stage). */
  appEnv: PlatformEnv;
  /** Wallet origin derived from `appEnv` via `WALLET_ORIGINS`. */
  walletOrigin: string;
  /**
   * Consumer payments surface (credit chips + Billing page). Not configured
   * here — read from the marketplace's `/api/config`, since whether the
   * surface exists is a property of that deployment and the vault reaches
   * payment-service only through its proxy. False only when the marketplace
   * says so; an unreachable marketplace leaves it on and the reads degrade.
   */
  paymentsEnabled: boolean;
}

let cached: Promise<RuntimeConfig> | null = null;

export function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  cached = (async () => {
    const env = Schema.parse(collectEnv());
    // Independent lookups — run together so boot waits on the slower one
    // rather than their sum.
    const [gw, marketplace] = await Promise.all([
      getGatewayInfo(env.VITE_S3_ENDPOINT),
      getMarketplaceConfig(env.VITE_MARKETPLACE_BASE_URL),
    ]);
    return {
      vaultBaseUrl: env.VITE_VAULT_BASE_URL,
      marketplaceBaseUrl: env.VITE_MARKETPLACE_BASE_URL,
      ddcCdnUrl: env.VITE_DDC_CDN_URL,
      ddcComputeUrl: env.VITE_DDC_COMPUTE_URL,
      s3Endpoint: env.VITE_S3_ENDPOINT ?? null,
      gatewayPublicKey: gw.gatewayPublicKey,
      clusterId: gw.clusterId,
      region: gw.region,
      garUrl: env.VITE_GAR_URL,
      rpcEndpoint: env.VITE_RPC_ENDPOINT,
      appEnv: env.VITE_APP_ENV,
      walletOrigin: WALLET_ORIGINS[env.VITE_APP_ENV],
      // Unknown is not off. An explicit `false` means this deployment has no
      // payments surface. A failed lookup means we could not find out — and
      // this promise is memoized for the page's lifetime with no retry, so
      // treating that as off would silently hide Billing from a subscriber
      // for their whole session over one transient blip. Rendering it reaches
      // the existing "Billing unavailable · Retry" state instead.
      paymentsEnabled: marketplace?.paymentsEnabled ?? true,
    };
  })();
  return cached;
}

export function resetRuntimeConfigForTests(): void {
  cached = null;
}
